import React, { FC, useEffect, useMemo, useState } from "react"
import { Modal, Form, Input, Switch, Button, Divider, Select, Tabs } from "antd"
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { useStore } from "stores/utils/use-store"
import {
  CreateGamePlatform,
  CreateGameTask,
  CreateNativeGameRequest,
  GamePlatformType,
  GamePublisherOption,
  GameTaskType,
} from "../store/types"
import { gamePlatformOptions, gameTaskTypeOptions } from "../constants"
import { notifications } from "notifications"
import Styles from "./styles.module.css"
import { getGamePublishers } from "../api/native-games-control.call"
import { PlatformFormFields } from "./platform-form-fields"
import { PlatformTabLabel } from "./platform-tab-label"

interface Props {
  open: boolean;
  onClose: () => void;
}

const GLOBAL_TAB_KEY = "__global__"

/**
 * Add-game modal driven by an editable-card Tabs strip.
 *
 * The tab list lives in local React state (`tabPlatforms`) so adding a tab via
 * `+` is synchronous and `activeKey` always matches an item. Form values for
 * the platform fields live in Antd's form store on ["platforms", idx, field]
 * paths — kept in sync with tabPlatforms on add/remove. Avoiding Form.useWatch
 * for the tabs UI because it can lag a render behind setFieldValue on a
 * previously-unset path, which led to the new tab's content rendering empty.
 *
 * Tasks-per-platform keep their nested Form.List on the
 * ["platforms", idx, "tasks"] path; that subtree doesn't share a render
 * boundary with the global fields so the original Form.List ergonomics work.
 */
export const AddNativeGameModal: FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm()
  const { dataStore: { nativeGamesControlStore } } = useStore()
  const [publishers, setPublishers] = useState<GamePublisherOption[]>([])
  const [activeTab, setActiveTab] = useState<string>(GLOBAL_TAB_KEY)
  const [tabPlatforms, setTabPlatforms] = useState<{ platform: GamePlatformType }[]>([])

  useEffect(() => {
    if (open) {
      getGamePublishers()
        .then(res => res && setPublishers(res))
        .catch(err => notifications.error(err?.message || "Failed to load publishers"))
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setTabPlatforms([])
      setActiveTab(GLOBAL_TAB_KEY)
    }
  }, [open, form])

  const availablePlatforms = useMemo(() => {
    const present = new Set<string>(tabPlatforms.map(p => p.platform))

    return gamePlatformOptions.filter(opt => !present.has(opt.value))
  }, [tabPlatforms])

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    if (!errorFields?.length) return
    const firstPath = errorFields[0].name
    if (Array.isArray(firstPath) && firstPath[0] === "platforms" && typeof firstPath[1] === "number") {
      const idx = firstPath[1]
      const platform = form.getFieldValue(["platforms", idx, "platform"])
      if (platform) setActiveTab(platform)
    } else {
      setActiveTab(GLOBAL_TAB_KEY)
    }
    notifications.error("Please fix the highlighted fields before saving")
  }

  const onFinishHandler = async(values: any) => {
    if (!values.platforms || values.platforms.length === 0) {
      notifications.error("Add at least one platform before saving")

      return
    }

    try {
      const platforms: CreateGamePlatform[] = (values.platforms || []).map((p: any) => ({
        platform: p.platform,
        platformId: p.platformId,
        iconUrl: p.iconUrl,
        rating: Number(p.rating),
        installRevenue: Number(p.installRevenue),
        adjustLinkToken: p.adjustLinkToken || null,
        ctaLink: p.ctaLink,
        ctaCampaign: p.ctaCampaign,
        attributionPlatform: p.attributionPlatform,
        gamePublisherId: p.gamePublisherId,
        appsflyerRevenue: p.appsflyerRevenue != null && p.appsflyerRevenue !== "" ? Number(p.appsflyerRevenue) : null,
        attributionWindowDays:
          p.attributionWindowDays != null && p.attributionWindowDays !== ""
            ? Number(p.attributionWindowDays)
            : null,
        isInstallable: p.isInstallable ?? true,
        isBudgetActive: p.isBudgetActive ?? false,
        dailyBudget: p.dailyBudget != null && p.dailyBudget !== "" ? Number(p.dailyBudget) : undefined,
        monthlyBudget: p.monthlyBudget != null && p.monthlyBudget !== "" ? Number(p.monthlyBudget) : undefined,
      }))

      const tasks: CreateGameTask[] = (values.platforms || []).flatMap((p: any) => (p.tasks || []).map((task: any) => ({
        name: task.name,
        platform: p.platform,
        // rewardUsd is required end-to-end so the form-level rule guarantees a value here.
        rewardUsd: Number(task.rewardUsd),
        sequence: Number(task.sequence),
        type: task.type,
        appsflyerRevenue: Number(task.appsflyerRevenue) || null,
        cpeRevenue: Number(task.cpeRevenue) || null,
        bonusRewardUsd: task.bonusRewardUsd != null && task.bonusRewardUsd !== "" ? Number(task.bonusRewardUsd) : null,
        expiresInMinutes: Number(task.expiresInMinutes) || null,
        iconNumber: Number(task.iconNumber),
        taskToken:
          task.type === GameTaskType.BONUS_TASK && (task.taskToken == null || String(task.taskToken).trim() === "")
            ? undefined
            : task.taskToken,
        ...(task.type === GameTaskType.WATCH_AN_ADS_TASK && { adsWatchedTotalRequired: Number(task.adsWatchedTotalRequired) }),
      })))

      const payload: CreateNativeGameRequest = {
        name: values.name,
        category: values.category,
        bannerUrl: values.bannerUrl,
        videoBannerUrl: values.videoBannerUrl || null,
        earningSpeed: Number(values.earningSpeed),
        description: values.description || null,
        isTopPick: values.isTopPick,
        isOnlyForEngagingAccount: values.isOnlyForEngagingAccount,
        platforms,
        tasks,
      }

      await nativeGamesControlStore.createNativeGame(payload)
      notifications.success("Game successfully created")
      form.resetFields()
      setTabPlatforms([])
      onClose()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to add game")
    }
  }

  const handleTabEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: "add" | "remove",
  ) => {
    if (action === "add") {
      const nextPlatform = availablePlatforms[0]?.value as GamePlatformType | undefined
      if (!nextPlatform) {
        notifications.error("No more platforms available to add")

        return
      }
      const nextTabPlatforms = [...tabPlatforms, { platform: nextPlatform }]
      setTabPlatforms(nextTabPlatforms)
      const current: any[] = form.getFieldValue("platforms") || []
      form.setFieldValue("platforms", [
        ...current,
        {
          platform: nextPlatform,
          isInstallable: true,
          isBudgetActive: false,
          tasks: [],
        },
      ])
      setActiveTab(nextPlatform)
    } else if (action === "remove" && typeof targetKey === "string") {
      const idx = tabPlatforms.findIndex(p => p.platform === targetKey)
      if (idx < 0) return
      setTabPlatforms(tabPlatforms.filter((_, i) => i !== idx))
      const current: any[] = form.getFieldValue("platforms") || []
      form.setFieldValue("platforms", current.filter((_, i) => i !== idx))
      setActiveTab(GLOBAL_TAB_KEY)
    }
  }

  return (
    <Modal
      title="Add Native Game"
      open={open}
      destroyOnClose
      onCancel={() => {
        form.resetFields()
        setTabPlatforms([])
        onClose()
      }}
      footer={null}
      width={780}
      bodyStyle={{ paddingTop: 0, paddingBottom: 12 }}
    >
      <Form layout="vertical" form={form} onFinish={onFinishHandler} onFinishFailed={onFinishFailed}>
        <Tabs
          type="editable-card"
          hideAdd={availablePlatforms.length === 0}
          activeKey={activeTab}
          onChange={setActiveTab}
          onEdit={handleTabEdit}
          items={[
            {
              key: GLOBAL_TAB_KEY,
              label: "Global",
              closable: false,
              children: (
                <>
                  <Divider orientation="left">Game Details</Divider>
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: "Game name is required" }]}
                  >
                    <Input placeholder="Please enter Game Name" />
                  </Form.Item>
                  <Form.Item
                    label="Category"
                    name="category"
                    rules={[{ required: true, message: "Category is required" }]}
                  >
                    <Input placeholder="Please enter category" />
                  </Form.Item>
                  <Form.Item
                    label="Banner URL"
                    name="bannerUrl"
                    rules={[
                      { required: true, message: "Banner URL is required" },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}
                  >
                    <Input placeholder="Please enter Banner URL" />
                  </Form.Item>
                  <Form.Item
                    label="Video Banner URL"
                    name="videoBannerUrl"
                    rules={[{ type: "url", message: "Please enter a valid URL" }]}
                  >
                    <Input placeholder="Please enter Video Banner URL" />
                  </Form.Item>
                  <Form.Item
                    label="Earning Speed"
                    name="earningSpeed"
                    rules={[{ required: true, message: "Earning Speed is required" }]}
                  >
                    <Input type="number" placeholder="Please enter Earning Speed" min={1} />
                  </Form.Item>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea placeholder="Please enter Description" />
                  </Form.Item>
                  <Form.Item label="Is Top Pick" name="isTopPick" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label="Engaging users only"
                    name="isOnlyForEngagingAccount"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  {tabPlatforms.length === 0 && (
                    <div style={{ background: "#fffbe6", padding: 12, borderRadius: 4, marginTop: 12 }}>
                      Add at least one platform tab (<strong>+</strong> on the tab strip) before saving.
                    </div>
                  )}
                </>
              ),
            },
            ...tabPlatforms.map((p, idx) => {
              const platformChoices = [
                ...availablePlatforms,
                ...gamePlatformOptions.filter(opt => opt.value === p.platform),
              ]

              return {
                key: p.platform,
                label: (
                  <PlatformTabLabel
                    platform={p.platform}
                    isInstallable={form.getFieldValue(["platforms", idx, "isInstallable"]) ?? true}
                  />
                ),
                closable: true,
                forceRender: true,
                children: (
                  <>
                    <PlatformFormFields
                      index={idx}
                      publishers={publishers}
                      platformLocked={false}
                      availablePlatforms={platformChoices}
                    />

                    <Divider orientation="left" style={{ fontSize: 14 }}>
                      Tasks for {p.platform}
                    </Divider>

                    <Form.List name={["platforms", idx, "tasks"]}>
                      {(taskFields, { add: addTask, remove: removeTask }) => (
                        <>
                          {taskFields.map(({ key, name, ...restField }, taskIdx) => (
                            <div key={key} style={{ position: "relative" }}>
                              {taskIdx > 0 && <Divider style={{ margin: "10px 0" }} />}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                <Form.Item
                                  {...restField}
                                  label="Task Name"
                                  name={[name, "name"]}
                                  rules={[{ required: true, message: "Task name is required" }]}
                                  style={{ width: 200 }}
                                >
                                  <Input placeholder="Please enter Task Name" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  label="Sequence"
                                  name={[name, "sequence"]}
                                  rules={[{ required: true, message: "Sequence is required" }]}
                                  style={{ width: 120 }}
                                >
                                  <Input type="number" min={1} placeholder="1" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  label="Reward ($)"
                                  name={[name, "rewardUsd"]}
                                  rules={[{ required: true, message: "Reward is required" }]}
                                  style={{ width: 120 }}
                                >
                                  <Input type="number" min={0} step={0.01} placeholder="USD" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  label="Type"
                                  name={[name, "type"]}
                                  rules={[{ required: true, message: "Task type is required" }]}
                                  style={{ width: 200 }}
                                >
                                  <Select options={gameTaskTypeOptions} placeholder="Task type" />
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prev, cur) => prev.platforms?.[idx]?.tasks?.[name]?.type
                                      !== cur.platforms?.[idx]?.tasks?.[name]?.type}
                                >
                                  {({ getFieldValue }) => (
                                    getFieldValue(["platforms", idx, "tasks", name, "type"]) === GameTaskType.WATCH_AN_ADS_TASK
                                      ? (
                                        <Form.Item
                                          {...restField}
                                          name={[name, "adsWatchedTotalRequired"]}
                                          label="Ads Required"
                                          style={{ width: 150 }}
                                          rules={[
                                            { required: true, message: "Ads watched total required" },
                                            {
                                              validator: (_, value) => (!value || Number(value) >= 1
                                                ? Promise.resolve()
                                                : Promise.reject(new Error("Must be at least 1"))),
                                            },
                                          ]}
                                        >
                                          <Input type="number" min={1} placeholder="e.g. 10" />
                                        </Form.Item>
                                      )
                                      : null
                                  )}
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "appsflyerRevenue"]}
                                  label="Appsflyer Revenue"
                                  style={{ width: 150 }}
                                >
                                  <Input type="number" min={0} step={0.01} placeholder="USD" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "cpeRevenue"]}
                                  label="CPE Revenue"
                                  style={{ width: 150 }}
                                >
                                  <Input type="number" min={0} step={0.01} placeholder="USD" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "bonusRewardUsd"]}
                                  label="Bonus Reward ($)"
                                  style={{ width: 130 }}
                                >
                                  <Input type="number" min={0} step={0.01} placeholder="USD" />
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prev, cur) => prev.platforms?.[idx]?.tasks?.[name]?.type
                                      !== cur.platforms?.[idx]?.tasks?.[name]?.type}
                                >
                                  {({ getFieldValue }) => {
                                    const isBonus
                                      = getFieldValue(["platforms", idx, "tasks", name, "type"])
                                        === GameTaskType.BONUS_TASK

                                    return (
                                      <Form.Item
                                        {...restField}
                                        name={[name, "taskToken"]}
                                        label="Task Token"
                                        rules={isBonus ? [] : [{ required: true, message: "Task Token is required" }]}
                                        style={{ width: 200 }}
                                      >
                                        <Input placeholder={isBonus ? "Optional for BONUS_TASK" : "e.g. reached_level_20"} />
                                      </Form.Item>
                                    )
                                  }}
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "expiresInMinutes"]}
                                  label="Expires in Min"
                                  style={{ width: 130 }}
                                >
                                  <Input type="number" min={1} placeholder="min" />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, "iconNumber"]}
                                  label="Icon number"
                                  style={{ width: 130 }}
                                  rules={[{ required: true, message: "Icon number is required" }]}
                                >
                                  <Input type="number" min={1} placeholder="1" />
                                </Form.Item>
                              </div>
                              <MinusCircleOutlined
                                onClick={() => removeTask(name)}
                                className={Styles.minusIcon}
                              />
                            </div>
                          ))}
                          <Form.Item>
                            <Button type="dashed" onClick={() => addTask()} icon={<PlusOutlined />} block>
                              Add Task for {p.platform}
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </>
                ),
              }
            }),
          ]}
        />

        {availablePlatforms.length > 0 && (
          <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
            Click <strong>+</strong> on the tab strip to add{" "}
            {availablePlatforms.map(p => p.label).join(" / ")} for this game.
          </div>
        )}

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <Button
              onClick={() => {
                form.resetFields()
                setTabPlatforms([])
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" disabled={tabPlatforms.length === 0}>
              Add Game
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}
